import React, { useState, useEffect } from "react";
import {
  Modal,
  Button,
  Form,
  FormGroup,
  FormControl,
  FormLabel,
  FormCheck,
} from "react-bootstrap";
import { CircularProgress } from "@mui/material";
import { Switch } from "@mui/material";
import { SketchPicker } from "react-color";
import CourseExerciseTabs from "../../utils/CourseExerciseTabs";
import { createData, updateData, deleteImage, fetchData } from "../../api/api";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import CloseIcon from "@mui/icons-material/Close";

export default function CourseModal(props) {
  const [modalContent, setModalContent] = useState("course");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);
    const isNewEntry = !props.courseData.id;

    try {
      // Create a FormData object for file uploads
      const formData = new FormData();

      // Add basic fields
      Object.entries(props.courseData).forEach(([key, value]) => {
        if (
          key !== "exercises" &&
          key !== "attachments" &&
          key !== "links" &&
          key !== "image" &&
          value !== undefined
        ) {
          formData.append(key, value);
        }
      });

      // Handle background image if present
      if (
        props.courseData.image &&
        typeof props.courseData.image !== "string"
      ) {
        formData.append("image", props.courseData.image);
      }

      // Handle exercises with audio files
      if (Array.isArray(props.courseData.exercises)) {
        props.courseData.exercises.forEach((exercise, index) => {
          formData.append(`exercises[${index}][name]`, exercise.name || "");
          formData.append(
            `exercises[${index}][description]`,
            exercise.description || ""
          );
          formData.append(
            `exercises[${index}][sort_order]`,
            exercise.sort_order || index
          );

          if (exercise.id) {
            formData.append(`exercises[${index}][id]`, exercise.id);
          }

          // Handle audio file
          if (exercise.audio && exercise.audio instanceof File) {
            formData.append(`exercises[${index}][audio]`, exercise.audio);
            console.log(
              `Including audio file for exercise ${index}:`,
              exercise.audio.name
            );
          }
        });
      }

      // Handle attachments
      if (
        props.courseData.attachments &&
        props.courseData.attachments.length > 0
      ) {
        props.courseData.attachments.forEach((attachment, index) => {
          if (attachment.file && attachment.file instanceof File) {
            formData.append(`attachments[${index}][file]`, attachment.file);
          }
          if (attachment.name) {
            formData.append(`attachments[${index}][name]`, attachment.name);
          }
          if (attachment.id) {
            formData.append(`attachments[${index}][id]`, attachment.id);
          }
        });
      }

      // Handle links
      if (props.courseData.links && props.courseData.links.length > 0) {
        props.courseData.links.forEach((link, index) => {
          formData.append(`links[${index}]`, link);
        });
      }

      if (!isNewEntry) {
        const response = await updateData(
          "programs",
          props.courseData.id,
          formData,
          true
        );

        // Use the response to update local state, including audio_url values
        if (response && response.exercises) {
          const updatedCourseData = {
            ...props.courseData,
            exercises: response.exercises.map((serverExercise) => {
              // Find matching local exercise by ID or index
              const matchingExercise =
                props.courseData.exercises.find(
                  (e) => e.id === serverExercise.id
                ) || {};

              return {
                ...matchingExercise,
                ...serverExercise,
                // Keep the local audio File object if it exists
                audio:
                  matchingExercise.audio instanceof File
                    ? matchingExercise.audio
                    : null,
                // Keep local preview URL if it exists, otherwise use server URL
                audioPreview: matchingExercise.audioPreview || null,
                // Ensure we have the ID and audio_url from server
                id: serverExercise.id,
                audio_url: serverExercise.audio_url,
              };
            }),
          };
          props.updateCourse(updatedCourseData);
        } else {
          props.updateCourse(props.courseData);
        }

        props.handleSnackbar("Kurs erfolgreich aktualisiert", "success");
        props.onHide();
        return;
      }

      const response = await createData("programs", formData, true);
      console.log("Create response:", response);

      // For new course, use the complete response including exercise IDs and audio URLs
      if (response && response.exercises) {
        const updatedCourseData = {
          ...props.courseData,
          id: response.id,
          exercises: response.exercises.map((serverExercise, index) => {
            // Find the local exercise data (by index for new exercises)
            const localExercise = props.courseData.exercises[index] || {};

            return {
              ...localExercise,
              ...serverExercise,
              // Keep the local audio File object if it exists
              audio:
                localExercise.audio instanceof File
                  ? localExercise.audio
                  : null,
              // Keep local preview URL if it exists
              audioPreview: localExercise.audioPreview || null,
              // Ensure we have the ID and audio_url from server
              id: serverExercise.id,
              audio_url: serverExercise.audio_url,
            };
          }),
        };
        props.updateCourse(updatedCourseData);
      } else {
        // Fallback if response doesn't include exercises
        const updatedCourseData = { ...props.courseData, id: response.id };
        props.updateCourse(updatedCourseData);
      }

      props.handleSnackbar("Kurs erfolgreich erstellt", "success");
      props.onHide();
    } catch (error) {
      console.error("Error in form submission:", error);
      props.handleSnackbar(
        "Fehler beim Erstellen/Bearbeiten des Kurses",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  const switchContent = (content) => {
    setModalContent(content);
  };

  return (
    <Modal {...props} size="lg" centered>
      <Form onSubmit={handleSubmit} encType="multipart/form-data">
        <Modal.Header closeButton>
          <Modal.Title>
            {modalContent === "course"
              ? "Schritt 1/2 - Neuen Kurs erstellen"
              : "Schritt 2/2 - Einheiten hinzufügen"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {modalContent === "course" ? (
            <CourseForm {...props} />
          ) : (
            <CourseExerciseTabs
              exercises={props.courseData.exercises || []}
              setExercises={(newExercises) => {
                props.setCourseData({
                  ...props.courseData,
                  exercises: newExercises,
                });
              }}
            />
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => switchContent("course")}>
            1. Kursübersicht
          </Button>
          <Button variant="primary" onClick={() => switchContent("exercise")}>
            2. Einheitenübersicht
          </Button>
          <Button
            type="submit"
            disabled={loading}
            style={{ minWidth: "110px" }}
          >
            {loading ? (
              <>
                <CircularProgress
                  size={18}
                  thickness={4}
                  style={{
                    color: "white",
                    marginRight: "8px",
                    verticalAlign: "middle",
                  }}
                />
                <span>Speichern...</span>
              </>
            ) : (
              "Speichern"
            )}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}

function CourseForm({ courseData, setCourseData }) {
  const [useImage, setUseImage] = useState(!!courseData.image);
  const [changeFontColor, setChangeFontColor] = useState(false);
  const [newLink, setNewLink] = useState("");
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const categories = await fetchData("categories");
        setCategories(categories);
      } catch (error) {
        console.error("Failed to fetch categories:", error);
      }
    };

    fetchCategories();
  }, []);

  useEffect(() => {
    const fetchSubcategories = async () => {
      if (!courseData.category_id) return;
      try {
        const category = categories.find((cat) => {
          const isMatch = cat.id === Number(courseData.category_id);
          return isMatch;
        });
        setSubcategories(category ? category.subcategories : []);
      } catch (error) {}
    };

    fetchSubcategories();
  }, [courseData.category_id, categories]);

  const sketchPickerStyles = {
    default: {
      picker: {
        width: "150px",
      },
      presetColors: {
        display: "none",
      },
    },
  };

  const handleColorChange = (color) => {
    setCourseData({ ...courseData, color: color.hex });
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    const newAttachments = files?.map((file) => ({ name: file.name, file }));
    setCourseData({
      ...courseData,
      attachments: [...(courseData.attachments || []), ...newAttachments],
    });
  };

  const handleAddLink = () => {
    if (newLink.trim() !== "") {
      setCourseData({
        ...courseData,
        links: [...(courseData.links || []), newLink],
      });
      setNewLink("");
    }
  };

  const handleRemoveAttachment = (index) => {
    const updatedAttachments = courseData.attachments.filter(
      (_, i) => i !== index
    );
    setCourseData({ ...courseData, attachments: updatedAttachments });
  };

  const handleRemoveLink = (index) => {
    const updatedLinks = courseData.links.filter((_, i) => i !== index);
    setCourseData({ ...courseData, links: updatedLinks });
  };

  const handleBgImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setCourseData({ ...courseData, image: file });
    } else {
      console.log("No file selected");
    }
  };

  const handleDeleteImage = async () => {
    const confirmation = window.confirm(
      "Sind Sie sicher, dass Sie das Bild dauerhaft löschen möchten?"
    );
    if (!confirmation) return;

    try {
      await deleteImage("programs/delete_image", courseData.id);
      setCourseData({ ...courseData, image: null, image_url: null });
      setUseImage(false);
    } catch (error) {
      console.error("Failed to delete image", error);
    }
  };

  const handleFontColorChange = (color) => {
    setCourseData({ ...courseData, font_color: color.hex });
  };

  return (
    <div>
      <FormGroup controlId="courseName">
        <FormLabel className="form-label">Name</FormLabel>
        <FormControl
          type="text"
          placeholder="Kursnamen angeben"
          value={courseData.name}
          onChange={(e) =>
            setCourseData({ ...courseData, name: e.target.value })
          }
        />
      </FormGroup>
      <FormGroup controlId="courseCategory">
        <FormLabel className="form-label">Kategorie</FormLabel>
        <FormControl
          as="select"
          value={courseData.category_id || ""}
          onChange={(e) =>
            setCourseData({ ...courseData, category_id: e.target.value })
          }
        >
          <option value="">Kategorie auswählen</option>
          {categories.map((category) => (
            <option key={category.id} value={category.id}>
              {category.name}
            </option>
          ))}
        </FormControl>
      </FormGroup>
      {courseData.category_id && (
        <FormGroup controlId="courseSubcategory">
          <FormLabel className="form-label">Unterkategorie</FormLabel>
          <FormControl
            as="select"
            value={courseData.subcategory_id || ""}
            onChange={(e) =>
              setCourseData({ ...courseData, subcategory_id: e.target.value })
            }
          >
            <option value="">Unterkategorie auswählen (optional)</option>
            {subcategories.map((subcategory) => (
              <option key={subcategory.id} value={subcategory.id}>
                {subcategory.name}
              </option>
            ))}
          </FormControl>
        </FormGroup>
      )}
      <FormGroup controlId="courseCategory">
        <FormLabel className="form-label">Bezeichnung der Einheiten</FormLabel>
        <FormControl
          type="text"
          placeholder="Einheiten"
          value={courseData.unit_name}
          onChange={(e) =>
            setCourseData({ ...courseData, unit_name: e.target.value })
          }
        />
      </FormGroup>
      <FormGroup controlId="courseDescription">
        <FormLabel className="form-label">Beschreibung</FormLabel>
        <FormControl
          as="textarea"
          rows={3}
          value={courseData.description}
          onChange={(e) =>
            setCourseData({ ...courseData, description: e.target.value })
          }
        />
      </FormGroup>
      <FormGroup controlId="courseFree">
        <FormLabel className="form-label">Kostenlos?</FormLabel>
        <FormCheck
          type="checkbox"
          label="Dieser Kurs ist kostenlos"
          checked={courseData.free || false}
          onChange={(e) =>
            setCourseData({ ...courseData, free: e.target.checked })
          }
        />
      </FormGroup>
      <FormGroup controlId="coursePrice">
        <FormLabel className="form-label">Preis</FormLabel>
        <FormControl
          type="number"
          step="0.01"
          min="0"
          placeholder="Preis in Euro"
          value={courseData.price}
          onChange={(e) =>
            setCourseData({ ...courseData, price: e.target.value })
          }
          disabled={courseData.free || false}
        />
      </FormGroup>
      <FormGroup controlId="courseOrderRequired">
        <FormLabel className="form-label">Reihenfolge verplichtend?</FormLabel>
        <FormCheck
          type="checkbox"
          label="Reihenfolge verpflichtend?"
          checked={
            courseData.orderRequired === "true" ||
            courseData.orderRequired === true
          }
          onChange={(e) =>
            setCourseData({
              ...courseData,
              orderRequired: e.target.checked.toString(),
            })
          }
        />
      </FormGroup>
      <FormGroup controlId="courseBgType">
        <FormLabel>Hintergrundtyp</FormLabel>
        <Switch
          checked={useImage}
          onChange={() => setUseImage((prev) => !prev)}
          name="useImage"
          color="primary"
        />
        {useImage ? "Bild" : "Farbe"}
      </FormGroup>
      {!useImage ? (
        <FormGroup controlId="courseColor">
          <FormLabel className="form-label">Hintergrundfarbe</FormLabel>
          <SketchPicker
            color={courseData.color}
            onChangeComplete={handleColorChange}
            styles={sketchPickerStyles}
          />
        </FormGroup>
      ) : (
        <FormGroup controlId="courseBgImage">
          <FormLabel className="form-label">
            HintergrundBild hochladen
          </FormLabel>
          {courseData.image_url ? (
            <div>
              <img src={courseData.image_url} alt="Preview" width="200" />
              <Button variant="danger" onClick={handleDeleteImage}>
                Bild löschen
              </Button>
            </div>
          ) : (
            <FormControl
              type="file"
              accept="image/*"
              onChange={handleBgImageChange}
            />
          )}
        </FormGroup>
      )}
      <FormGroup controlId="courseFontColorChange">
        <FormLabel>Schriftfarbe ändern?</FormLabel>
        <Switch
          checked={changeFontColor}
          onChange={() => setChangeFontColor((prev) => !prev)}
          name="changeFontColor"
          color="primary"
        />
      </FormGroup>
      {changeFontColor && (
        <FormGroup controlId="courseFontColor">
          <FormLabel className="form-label">Schriftfarbe</FormLabel>
          <SketchPicker
            color={courseData.font_color || "#000000"}
            onChangeComplete={handleFontColorChange}
            styles={sketchPickerStyles}
            className="custom-sketch-picker"
          />
        </FormGroup>
      )}
      <FormGroup controlId="courseStartDate">
        <FormLabel className="form-label">Startdatum: </FormLabel>
        <DatePicker
          selected={courseData.valid_from}
          onChange={(date) =>
            setCourseData({ ...courseData, valid_from: date })
          }
          dateFormat="yyyy-MM-dd"
          className="form-control"
          placeholderText="Startdatum wählen"
        />
      </FormGroup>
      <FormGroup controlId="courseEndDate">
        <FormLabel className="form-label">Enddatum: </FormLabel>
        <DatePicker
          selected={courseData.valid_till}
          onChange={(date) =>
            setCourseData({ ...courseData, valid_till: date })
          }
          dateFormat="yyyy-MM-dd"
          className="form-control"
          placeholderText="Enddatum wählen"
        />
      </FormGroup>
      <FormGroup controlId="attachments">
        <FormLabel className="form-label">Anhänge</FormLabel>
        <div
          style={{
            border: "2px dashed #ddd",
            padding: "20px",
            textAlign: "center",
          }}
        >
          <input
            type="file"
            accept="application/pdf"
            multiple
            onChange={handleFileChange}
            style={{ display: "none" }}
            id="file-input"
          />
          <label htmlFor="file-input" style={{ cursor: "pointer" }}>
            <div>+ PDF hochladen</div>
          </label>
        </div>
        <ul>
          {courseData.attachments?.map((attachment, index) => (
            <li
              key={index}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <span>{attachment.name}</span>
              <CloseIcon
                style={{ cursor: "pointer", marginLeft: "10px" }}
                onClick={() => handleRemoveAttachment(index)}
              />
            </li>
          ))}
        </ul>
      </FormGroup>
      <FormGroup controlId="links">
        <FormLabel className="form-label">Links</FormLabel>
        <div style={{ display: "flex", marginBottom: "10px" }}>
          <FormControl
            type="text"
            placeholder="https://google.com -> bitte https:// hinzufügen"
            value={newLink}
            onChange={(e) => setNewLink(e.target.value)}
          />
          <Button onClick={handleAddLink} style={{ marginLeft: "10px" }}>
            Hinzufügen
          </Button>
        </div>
        <ul>
          {courseData.links?.map((link, index) => (
            <li key={index} style={{ display: "flex", alignItems: "center" }}>
              <a
                href={link}
                target="_blank"
                rel="noopener noreferrer"
                style={{ marginRight: "10px" }}
              >
                {link}
              </a>
              <CloseIcon
                style={{ cursor: "pointer" }}
                onClick={() => handleRemoveLink(index)}
              />
            </li>
          ))}
        </ul>
      </FormGroup>
      <FormGroup controlId="courseCoupon">
        <FormLabel className="form-label">Gutscheincode (optional)</FormLabel>
        <FormControl
          type="text"
          placeholder="Gutscheincode eingeben"
          value={courseData.coupon || ""}
          pattern=".{8,}"
          title="Gutscheincode muss mindestens 8 Zeichen lang sein."
          onChange={(e) =>
            setCourseData({ ...courseData, coupon: e.target.value })
          }
        />
      </FormGroup>
    </div>
  );
}

CourseModal.defaultProps = {
  courseData: {
    name: "",
    category: "",
    description: "",
    color: "#ffffff",
    orderRequired: false,
  },
  setCourseData: () => {},
};
